import {
  BentoTypographyVariant,
  fontFamilySansRegular,
  getTypographyVariant,
} from 'theme/typography';
import { CSSInterpolation, SerializedStyles } from '@emotion/serialize';
import { Shadows } from '@material-ui/core/styles/shadows';
import { ThemeName, cssColorMapping, cssThemeMapping, themes } from 'theme/colors/v2';
import { ThemeVarsObject, bento } from '@tock/tokens';
import { agGridColorTokens, agGridColors } from './colors/agGridColors';
import { createTheme } from '@material-ui/core/styles';
import { css } from '@emotion/react';

const colors = { ...themes.colors, agGrid: agGridColors };
const bentoColors = { ...bento.color, agGrid: agGridColors };

declare module '@material-ui/core/styles/createTypography' {
  interface TypographyOptions {
    variant: (variant: BentoTypographyVariant) => SerializedStyles;
  }
  interface Typography {
    variant: typeof getTypographyVariant;
  }
}

declare module '@material-ui/core/styles/shape' {
  interface Shape {
    /**
     * @deprecated
     */
    borderRadius: number;
  }
}

declare module '@material-ui/core/styles/createTheme' {
  interface Theme {
    layout: {
      hideScrollbar: string;
    };

    shadow: {
      100: string | `var(--${string})`;
      200: string | `var(--${string})`;
    };

    /**
     * @deprecated This has been replaced with the custom key 'shadow'.
     */
    shadows: Shadows;

    colors: ThemeVarsObject['color'];

    borderRadius: {
      /*
       * The smaller border radius
       */
      100: string | `var(--${string})`;
      /*
       * The larger border radius
       */
      200: string | `var(--${string})`;
      /*
       * This borderRadius provides completely rounded edges like a pill shape
       */
      Pill: '999em' | `var(--${string})`;
    };

    interactiveStates: {
      active: (val: CSSInterpolation) => CSSInterpolation;
      checked: (val: CSSInterpolation) => CSSInterpolation;
      disabled: (val: CSSInterpolation) => CSSInterpolation;
      focus: (val: CSSInterpolation) => CSSInterpolation;
      hover: (val: CSSInterpolation) => CSSInterpolation;
    };
  }
  // allow configuration using `createMuiTheme`
  interface ThemeOptions {
    layout: {
      hideScrollbar: string;
    };

    shadow: {
      100: string | `var(--${string})`;
      200: string | `var(--${string})`;
    };

    /**
     * @deprecated This has been replaced with the custom key 'shadow'.
     */
    shadows?: Shadows;

    colors: ThemeVarsObject['color'];

    borderRadius: {
      /*
       * The smaller border radius
       */
      100: string | `var(--${string})`;
      /*
       * The larger border radius
       */
      200: string | `var(--${string})`;
      /*
       * This borderRadius provides completely rounded edges like a pill shape
       */
      Pill: '999em' | `var(--${string})`;
    };

    interactiveStates: {
      active: (val: CSSInterpolation) => CSSInterpolation;
      checked: (val: CSSInterpolation) => CSSInterpolation;
      disabled: (val: CSSInterpolation) => CSSInterpolation;
      focus: (val: CSSInterpolation) => CSSInterpolation;
      hover: (val: CSSInterpolation) => CSSInterpolation;
    };
  }
}

export const noShadow = {
  boxShadow: 'none',
  '&:hover': {
    boxShadow: 'none',
  },
  '&:active': {
    boxShadow: 'none',
  },
  '&:focus': {
    boxShadow: 'none',
  },
  '@media (hover: none)': {
    boxShadow: 'none',
  },
};

const materialShadowsLight = {
  one: '0px 2px 2px -1px rgba(0, 0, 0, 0.1), 0px 1px 1px rgba(0, 0, 0, 0.1),  0px 1px 2px rgba(0, 0, 0, 0.12)',
  two: '0px 2px 4px -1px rgba(0, 0, 0, 0.1), 0px 3px 4px rgba(0, 0, 0, 0.1),  0px 1px 8px rgba(0, 0, 0, 0.12)',
};

const materialShadowsDark = {
  one: '0px 2px 2px rgba(0, 0, 0, 0.32),  0px 4px 48px rgba(255, 255, 255, 0.04), inset 0px 0px 2px rgba(255, 255, 255, 0.16)',
  two: '0px 4px 4px rgba(0, 0, 0, 0.64),  0px 6px 48px rgba(255, 255, 255, 0.04), inset 0px 0px 2px rgba(255, 255, 255, 0.16)',
};

const materialShadows = {
  light: materialShadowsLight,
  dark: materialShadowsDark,
};

export const getTheme = (type: ThemeName = 'light') => {
  const materialBoxShadows = materialShadows[type];
  const colorTokens = { ...cssColorMapping[type], ...cssThemeMapping };
  const muiPalette = themes.palette[type];

  const theme = createTheme({
    palette: {
      type,
      background: {
        default: muiPalette.smokeLightest,
        paper: muiPalette.smokeLightest,
      },
      primary: {
        main: muiPalette.royalBase,
      },
      secondary: {
        main: muiPalette.smokeDark,
      },
      error: {
        main: muiPalette.watermelonBase,
      },
      success: {
        main: muiPalette.appleBase,
      },
      warning: {
        main: muiPalette.blazeBase,
      },
      text: {
        primary: muiPalette.smokeDarkest,
      },
      action: {
        disabled: muiPalette.smokeLight,
        disabledBackground: muiPalette.smokeBase,
      },
    },
    layout: {
      hideScrollbar: `
      ::-webkit-scrollbar {
        display: none;
      }
      -ms-overflow-style: none;
      scrollbar-width: none;
      `,
    },
    shadow: {
      100: bento.shadow[100],
      200: bento.shadow[200],
    },
    colors: bentoColors,
    typography: {
      fontFamily: fontFamilySansRegular,
      htmlFontSize: 10,
      variant: getTypographyVariant,
    },
    borderRadius: {
      100: bento.borderRadius[100],
      200: bento.borderRadius[200],
      Pill: bento.borderRadius.Pill,
    },
    interactiveStates: {
      active: (val: CSSInterpolation) => css`
        &:active:not(:disabled):not([aria-disabled='true']) {
          ${val};
        }
      `,
      checked: (val: CSSInterpolation) => css`
        &:checked {
          ${val};
        }
      `,
      disabled: (val: CSSInterpolation) => css`
        &:disabled,
        &[aria-disabled='true'] {
          ${val};
        }
      `,
      focus: (val: CSSInterpolation) => css`
        body.no-focus-visible &:focus {
          ${val};
        }

        &:focus-visible {
          ${val};
        }
      `,
      hover: (val: CSSInterpolation) => css`
        @media (hover: hover) {
          &:hover:not(:disabled):not([aria-disabled='true']):not(:active) {
            ${val};
          }
        }
      `,
    },
    overrides: {
      MuiCssBaseline: {
        '@global': {
          '*': {
            fontVariantNumeric: 'tabular-nums',
            // NOTE: This update sets the stylistic set of Oracle to 16 which fixes an issue with the "i" character on low res monitors. This does not have any affect on our other font Sohne but if you run into issue with a font change in the future please see if this is the cause. This needs to be applied at both the * and body level to function everywhere.
            fontFeatureSettings: '"ss16" 1',
          },
          body: {
            fontVariantNumeric: 'tabular-nums',
            // NOTE: This update sets the stylistic set of Oracle to 16 which fixes an issue with the "i" character on low res monitors. This does not have any affect on our other font Sohne but if you run into issue with a font change in the future please see if this is the cause. This needs to be applied at both the * and body level to function everywhere.
            fontFeatureSettings: '"ss16" 1',
            '& .ag-theme-tock': {
              ...agGridColorTokens,
            },
          },

          ':root': {
            ...colorTokens,
            // temp special tokens where the mapping is not at the color level
            // testing here, will evaluate methods of putting them on the theme object/fully adopting
            ['--bg-overlay']: type === 'light' ? '#00000080' : `#00000080`,

            // force light mode for printing
            '@media print': {
              ...cssColorMapping.light,
            },
          },
        },
      },
      // Style sheet name ⚛️
      MuiButton: {
        // Name of the rule
        root: {
          ...noShadow,
        },
        contained: {
          ...noShadow,
          padding: '4px 16px',
        },
        outlined: {
          padding: '3px 16px',
        },
        text: {
          padding: '4px 8px',
        },
        containedPrimary: {
          ...noShadow,
        },
        containedSecondary: {
          ...noShadow,
        },
        outlinedSizeSmall: {
          padding: '4px 9px',
        },
        containedSizeSmall: {
          padding: '5px 10px',
        },
        textSizeSmall: {
          padding: '5px 10px',
        },
        outlinedSizeLarge: {
          padding: '7px 21px',
        },
        containedSizeLarge: {
          padding: '8px 22px',
        },
        textSizeLarge: {
          padding: '8px 22px',
        },
        label: {
          textTransform: 'none',
        },
      },
      MuiFormHelperText: {
        root: {
          color: colors.fgSubtle,
          lineHeight: 1.33, // same as caption typography

          '&$disabled': {
            color: colors.fgSubtle,
          },
        },
      },
      MuiTableCell: {
        root: {
          padding: '12px',
          verticalAlign: 'text-top',
        },
        head: {
          color: colors.fgDefault,
          verticalAlign: 'middle',
        },
        alignRight: {
          fontFeatureSettings: `'tnum' 1`,
        },
      },
      MuiSvgIcon: {
        ...{
          root: {
            fontSize: '2rem',
          },

          fontSizeSmall: {
            fontSize: '1.6rem',
          },

          fontSizeLarge: {
            fontSize: '2.4rem',
          },
        },
      },
      MuiDivider: {
        root: {
          backgroundColor: colors.borderDefault,
        },
      },
      MuiPaper: {
        outlined: { borderColor: colors.borderDefault },
      },
      MuiFab: {
        root: {
          '&$disabled': {
            boxShadow: materialBoxShadows.two,
          },
        },
      },
      MuiBackdrop: {
        root: {
          backgroundColor: 'var(--bg-overlay, rgba(0,0,0,0.5))',
        },
      },
      MuiPopover: {
        paper: {
          border: `1px solid ${colors.borderDefault}`,
        },
      },
      MuiFormLabel: {
        root: {
          '&.Mui-disabled': {
            color: colors.fgDisabled,
          },
        },
      },
      MuiFormControlLabel: {
        label: {
          '&.Mui-disabled': {
            color: colors.fgDisabled,
          },
        },
      },
      MuiListItem: {
        root: {
          gap: 8,
        },
      },
      MuiListItemIcon: {
        root: {
          minWidth: 'auto',
        },
      },
      MuiListSubheader: {
        root: {
          fontSize: '1.2rem',
          fontWeight: 400,
          lineHeight: 1,
          padding: '24px 0 8px',
        },
      },
    },
    props: {
      MuiButtonBase: { disableRipple: true, disableTouchRipple: true },
    },
  });

  // NOTE: These will not be used going forward to instead use "shadow" from the emotion theme which will be defined above.
  // overriding after creation in order to not supply all shadows.
  // https://github.com/mui-org/material-ui/issues/8780
  theme.shadows[1] = bento.shadow[100];
  theme.shadows[2] = bento.shadow[200];

  return theme;
};
